'use client';

import React, { useEffect, useState } from 'react';

import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import StarBorder from '@mui/icons-material/StarBorder';
import Box from '@mui/material/Box';
import { visuallyHidden } from '@mui/utils';

import {
  HomepageWelcomeCarouselCard,
  type HomepageWelcomeCarouselItem,
} from '@/components/HomepageWelcomeCarouselCard';
import { useSnackbar } from '@/context/SnackbarContext';
import { useUserInfo } from '@/context/UserInfoContext';
import { setUserWelcomed } from '@/lib/api/generated';

/** Supplies the decorative icon content rendered inline with the welcome tip text. */
interface InlineDecorativeIconProps {
  children: React.ReactNode;
}

/** Renders a decorative inline icon wrapper that stays out of screen-reader output. */
function InlineDecorativeIcon({ children }: InlineDecorativeIconProps) {
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        display: 'inline-block',
        verticalAlign: 'text-bottom',
        lineHeight: 0,
        mx: 0.25,
      }}
    >
      {children}
    </Box>
  );
}

/** Stores the fixed homepage welcome tips in the approved source-branch order. */
const HOME_WELCOME_ITEMS: HomepageWelcomeCarouselItem[] = [
  {
    text: 'Welcome to SureShop!  We make it easy to learn about products and to decide with confidence.  Ask about anything and get instant answers and recommendations, or collaborate with friends for their expertise and personal experience.  Get started by asking a question or describing what you’re looking for in the search box below!',
  },
  {
    text: 'Conversations can include you, an AI assistant, and friends.  The AI will answer your questions and find products for you.  Tag your friends to get their feedback, opinions, or have them help guide the AI in the right direction for you.  Posts are public by default so others can help, but you can also post to just friends, or privately between you and the AI.',
  },
  {
    text: 'Here on the home page, you’ll find your recent conversations as well as friends’ conversations.  You can continue your own conversations and help your friends by contributing to theirs.  You’ll also find options to add friends and discover more about the site.',
  },
  {
    text: 'Want a friend’s opinion on your options?  Want some validation that you and the AI are going in the right direction?  Invite your friend to SureShop to join the conversation, or simply use the @ sign to tag them if they’re already here.  You can invite from conversations or from the search-bar menu.',
  },
  {
    text: (
      <React.Fragment>
        {'If you see a product or post you want to save, select the '}
        <Box component="span" sx={visuallyHidden}>
          star
        </Box>
        <InlineDecorativeIcon>
          <StarBorder fontSize="inherit" />
        </InlineDecorativeIcon>
        {
          ' icon.  You can find your starred items page under the search box menu.  We’ll track price history for your starred products, too.'
        }
      </React.Fragment>
    ),
  },
  {
    text: (
      <React.Fragment>
        {'We love your feedback and suggestions.  Select Give feedback'}
        <InlineDecorativeIcon>
          <FeedbackOutlinedIcon fontSize="inherit" />
        </InlineDecorativeIcon>
        {' to share your thoughts on anything that isn’t great while you’re using SureShop!'}
      </React.Fragment>
    ),
  },
  {
    text: 'You can control all aspects of your account – password, friends, preferred marketplaces – by selecting the account option in the search box menu.',
  },
];

/** Renders the signed-in welcome tips carousel until the user dismisses it successfully. */
const HomeWelcomeSection: React.FC = () => {
  const { userInfo, reloadUserInfo } = useUserInfo();
  const { showSnackbar } = useSnackbar();
  const [showWelcomeCard, setShowWelcomeCard] = useState(false);
  /** Tracks whether the persistent dismissal request is currently in flight. */
  const [isDismissing, setIsDismissing] = useState(false);

  useEffect(() => {
    setShowWelcomeCard(userInfo ? !userInfo.has_been_welcomed : false);
  }, [userInfo]);

  /** Persists the welcomed state and hides the carousel only after success. */
  const handleDismiss = async () => {
    setIsDismissing(true);
    try {
      await setUserWelcomed({ has_been_welcomed: true });
      setShowWelcomeCard(false);
      void reloadUserInfo();
    } catch (error: unknown) {
      const isHttpFailure =
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        error.status !== undefined;
      showSnackbar({
        severity: 'error',
        message: isHttpFailure
          ? 'Failed to save your preference. Please try again.'
          : 'Connection error. Please check your internet and try again.',
      });
    } finally {
      setIsDismissing(false);
    }
  };

  return showWelcomeCard ? (
    <HomepageWelcomeCarouselCard
      items={HOME_WELCOME_ITEMS}
      onDismiss={handleDismiss}
      isDismissing={isDismissing}
    />
  ) : null;
};

export default HomeWelcomeSection;
